import { Box, Button, Typography } from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { getToken } from "../../utils/token";
import { FaPlus } from "react-icons/fa";
import deleteLogo from "../../assets/redDeleteBtn.png";
import BoldLabel from "../../UIComponents/BoldLevel";
import MuiSelectAndSearch from "../../UIComponents/MuiSelectAndSearch";
import MuiInput from "../../UIComponents/MuiInput";
import { useDispatch, useSelector } from "react-redux";
import MuiSelect from "../../UIComponents/MuiSelect";
import arrow from "../../assets/arrow-left.svg";
import MuiTextarea from "../../UIComponents/MuiTextarea";
import { getWhereSectionSelectOne } from "../../Redux/CreateRuleReducer/action";
import CreateRuleAttributeDrawer from "./CreateRuleAttributeDrawer";
import { resetCreateRuleData } from "../../Redux/CreateRuleReducer/actionType";

const options = [
  { label: "AND", value: "AND" },
  { label: "OR", value: "OR" },
];
const colors = [
  "#5A82BF", // Darker blue
  "#F4F8FC", // Original (very light blue)
  "#D9E5F2", // Slightly darker
  "#BFD2E8", // More visible light blue
  "#A6BEDD", // Soft medium blue
  "#8CAAD3", // Noticeably darker
  "#7396C9", // Medium-dark blue
];


function convertToRuleConfiguration(data) {
  let isError = false;
  function processGroup(items, parentCondition = null) {
    if (isError) return;
    return items.map((item, index) => {
      if (isError) return;
      if (item.isGroup || item?.groupDetail?.length > 0) {
        // Handle group
        const groupItems = item.groupDetail || item.group;
        return {
          sequenceId: 1 + index,
          isGroup: true,
          condition:
            index > 0 ? item.condition?.toLowerCase() || parentCondition : null,
          group: processGroup(groupItems, item.condition),
        };
      } else {
        if (!item?.attributeName?.name || !item?.operator?.name || !item?.value) {
          isError = true;
          toast.error("All fields are required");
          return;
        }
        return {
          sequenceId: 1 + index,
          isGroup: false,
          attributeName: item.attributeName?.name || null,
          entityName: item.attributeName?.entityName || null,
          attributeId: item.attributeName?.id?.toString() || null,
          type: item.operator?.name || null,
          valueType: item.attributeName?.attributeValueType || null,
          value: item.value || null,
          condition:
            index > 0 ? item.condition?.toLowerCase() || parentCondition : null,
        };
      }
    });
  }
  const result = processGroup(data);
  if (isError) return null;

  return result;
}

function convertToOriginalFormat(newData) {
  function findOperatorById(operatorName) {
    const operatorMap = {
      EQUALS: 0,
      NOT_EQUALS: 1,
      LESS_THAN: 2,
      GREATER_THAN: 3,
      LESS_THAN_OR_EQUAL: 4,
      GREATER_THAN_OR_EQUAL: 5,
      CONTAINS: 6,
      NOT_CONTAINS: 7,
    };
    return operatorMap[operatorName] || 0;
  }

  function getDefaultOperators() {
    return [
      { id: 0, name: "EQUALS" },
      { id: 1, name: "NOT_EQUALS" },
      { id: 2, name: "LESS_THAN" },
      { id: 3, name: "GREATER_THAN" },
      { id: 4, name: "LESS_THAN_OR_EQUAL" },
      { id: 5, name: "GREATER_THAN_OR_EQUAL" },
      { id: 6, name: "CONTAINS" },
      { id: 7, name: "NOT_CONTAINS" },
    ];
  }

  function processGroup(items, parentId = Date.now()) {
    return items.map((item, index) => {
      const baseId = parentId + index;

      if (item.isGroup) {
        const groupStructure = {
          id: baseId,
          isGroup: false,
          attributeName: null,
          entityName: null,
          attributeId: null,
          type: null,
          valueType: null,
          value: null,
          condition: item.condition?.toUpperCase() || "OR",
          operator: null,
          group: true,
          groupDetail: processGroup(item.group, baseId + 1000),
        };

        // If the first item in the group has attribute information, copy it to the parent
        const firstGroupItem = item.group[0];
        if (firstGroupItem && !firstGroupItem.isGroup) {
          groupStructure.attributeName = {
            id: parseInt(firstGroupItem.attributeId),
            name: firstGroupItem.attributeName,
            attributeValueType: firstGroupItem.valueType,
            entityName: firstGroupItem.entityName,
            operator: getDefaultOperators(),
          };
          groupStructure.value = firstGroupItem.value;
          groupStructure.operator = {
            id: findOperatorById(firstGroupItem.type),
            name: firstGroupItem.type,
          };
        }

        return groupStructure;
      } else {
        return {
          id: baseId,
          isGroup: false,
          attributeName: {
            id: parseInt(item.attributeId),
            name: item.attributeName,
            attributeValueType: item.valueType,
            entityName: item.entityName,
            operator: getDefaultOperators(),
          },
          entityName: null,
          attributeId: null,
          type: null,
          valueType: null,
          value: item.value,
          condition: item.condition?.toUpperCase() || "OR",
          operator: {
            id: findOperatorById(item.type),
            name: item.type,
          },
          group: false,
          groupDetail: [],
        };
      }
    });
  }

  return processGroup(newData);
}

// NestedItem component remains exactly the same as before
const NestedItem = ({
  item,
  parentId,
  parentPath,
  onAddToSelf,
  onAddToGroup,
  onDelete,
  onEdit,
  depth = 0,
  index,
  allData,
  state,
  pathname,
}) => {
  const roleOptionData = useSelector(
    (store) => store.CreateRuleReducer.select1
  );
  const roleOption = roleOptionData.map((item) => ({
    id: item?.id,
    name: item?.attributeName,
    attributeValueType: item?.attributeValueType,
    entityName: item?.entityName,
    operator: item?.operator.map((e, index) => {
      return { id: index, name: e };
    }),
  }));

  const handleRoleChange = (e) => {
    onEdit(item.id, parentId, "attributeName", e);
    onEdit(item.id, parentId, "operator", null);
    onEdit(item.id, parentId, "value", null);
  };

  const handleOperatorChange = (e) => {
    onEdit(item.id, parentId, "operator", e);
  };
  const handleValueChange = (e) => {
    onEdit(item.id, parentId, "value", e);
  };
  const handleConditionChange = (e) => {
    onEdit(item.id, parentId, "condition", e);
  };

  if (item.group) {
    return (
      <Box>
        {index > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "96%",
              gap: "26px",
              borderRadius: "8px",
              padding: "25px",
              height: "32px",
            }}
          >
            <Box sx={{ border: "1px solid #0B1B32", width: "100%" }}></Box>
            <Box sx={{ width: "180px" }}>
              <MuiSelect
                marginTop="0px"
                backgroundColor="black"
                selectedBackgroundColor="black"
                textColor="white"
                selectedTextColor="white"
                hoverBackgroundColor="gray"
                options={options}
                value={item.condition}
                onChange={(e) => onEdit(item.id, parentId, "condition", e)}
                isDisabled={state?.preview || pathname === "/preview-rule"}
              />
            </Box>
            <Box sx={{ border: "1px solid #0d1520", width: "100%" }}></Box>
          </Box>
        )}
        <Box
          style={{
            backgroundColor: colors[parentPath.length % colors.length],
            borderRadius: "8px",
            margin: "8px",
            padding: "16px",
          }}
        >
          <Box style={{ marginLeft: "0px" }}>
            {item.groupDetail.map((subItem, index) => (
              <Box key={`${depth}-${subItem.id}`}>
                <NestedItem
                  item={subItem}
                  parentId={item.id}
                  parentPath={`${parentPath}${index + 1}`}
                  onAddToSelf={onAddToSelf}
                  onAddToGroup={onAddToGroup}
                  onDelete={onDelete}
                  onEdit={onEdit}
                  depth={depth + 1}
                  index={index}
                  allData={item.groupDetail}
                  state={state}
                  pathname={pathname}
                />
              </Box>
            ))}
          </Box>
          <Box
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              marginTop: "14px",
            }}
          >
            {/* Group delete button - Fixed to pass correct IDs */}
            <Button
              sx={{
                minWidth: "32px",
                width: "32px",
                height: "32px",
                borderRadius: "6px",
                backgroundColor: "#FC55551A",
                "&:hover": {
                  backgroundColor: "#FC55551A",
                },
                display: allData?.length === 1 ? "none" : "",
                "&:disabled": {
                  backgroundColor: "#FC55551A",
                },
              }}
              onClick={() => onDelete(item.id, parentId)}
              disabled={state?.preview || pathname === "/preview-rule"}
            >
              <img
                style={{ height: "25px", width: "25px" }}
                src={deleteLogo}
                alt="deleteicon"
              />
            </Button>

            <Button
              onClick={() => onAddToGroup(item.id)}
              variant="outline"
              size="sm"
              style={{
                marginLeft: "8px",
                color: "#003D86",
                fontWeight: 700,
                fontSize: "12px",
                textTransform: "capitalize",
                "&:disabled": {
                  color: "#003D86",
                },
              }}
              disabled={state?.preview || pathname === "/preview-rule"}
            >
              + Add To Group
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      style={{
        borderRadius: "8px",
      }}
    >
      {item?.condition && index > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            gap: "26px",
            borderRadius: "8px",
            padding: "25px",
            height: "32px",
          }}
        >
          <Box sx={{ border: "1px solid #0B1B32", width: "100%" }}></Box>
          <Box sx={{ width: "180px" }}>
            <MuiSelect
              marginTop="5px"
              backgroundColor="black"
              selectedBackgroundColor="black"
              textColor="white"
              selectedTextColor="white"
              hoverBackgroundColor="gray"
              options={options}
              value={item.condition}
              onChange={(e) => handleConditionChange(e)}
              isDisabled={state?.preview || pathname === "/preview-rule"}
            />
          </Box>
          <Box sx={{ border: "1px solid #0d1520", width: "100%" }}></Box>
        </Box>
      )}
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          backgroundColor: colors[parentPath.length % colors.length],
          padding: "14px",
          borderRadius: "8px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: "20px",
            alignItems: "end",
            width: "80%",
          }}
        >
          <Box style={{ width: "50%" }}>
            <BoldLabel label="Rule" isRequired={true} mt="0px" />
            <MuiSelectAndSearch
              options={roleOption}
              value={item.attributeName}
              placeholder={"Search / Select"}
              onChange={(e) => handleRoleChange(e)}
              isDisable={state?.preview || pathname === "/preview-rule"}
            />
          </Box>
          <Box style={{ width: "50%" }}>
            <BoldLabel label="Operator" isRequired={true} mt="0px" />
            <MuiSelectAndSearch
              options={item?.attributeName?.operator ?? []}
              value={item?.operator}
              placeholder={"Search / Select"}
              onChange={(e) => handleOperatorChange(e)}
              isDisable={state?.preview || pathname === "/preview-rule"}
            />
          </Box>
          {console.log(item?.attributeName, "test")}
          <Box sx={{ width: "50%" }}>
            <BoldLabel
              label={`Add Value ${
                item?.attributeName?.attributeValueType === "NUMBER"
                  ? "(e.g., 123)"
                  : item?.attributeName?.attributeValueType === "STRING"
                  ? "(e.g., Text)"
                  : item?.attributeName?.attributeValueType === "BOOLEAN"
                  ? "(e.g., true or false)"
                  : item?.attributeName?.attributeValueType === "ARRAY"
                  ? "(comma-separated values)"
                  : ""
              }`}
              isRequired={true}
              mt="0px"
            />
            <MuiInput
              type={
                item?.attributeName?.attributeValueType === "NUMBER"
                  ? "number"
                  : "text"
              }
              placeholder={
                item?.attributeName?.attributeValueType === "NUMBER"
                  ? "(e.g., 123)"
                  : item?.attributeName?.attributeValueType === "STRING"
                  ? "(e.g., Text)"
                  : item?.attributeName?.attributeValueType === "BOOLEAN"
                  ? "(e.g., true or false)"
                  : item?.attributeName?.attributeValueType === "ARRAY"
                  ? "(e.g., abc, def)"
                  : "Enter value"
              }
              value={item.value}
              onChange={(e) => handleValueChange(e)}
              disable={state?.preview || pathname === "/preview-rule"}
            />
          </Box>
          <Button
            sx={{
              minWidth: "32px",
              height: "32px",
              borderRadius: "6px",
              backgroundColor: "#FC55551A",
              mb: "4px",
              "&:hover": {
                backgroundColor: "#FC55551A",
              },
              "&:disabled": {
                cursor: "not-allowed",
              },
              display:
                allData?.length === 1 && !allData[0]?.group ? "none" : "",
            }}
            disabled={state?.preview || pathname === "/preview-rule"}
            onClick={() => onDelete(item.id, parentId)}
          >
            <img
              style={{ height: "25px", width: "25px" }}
              src={deleteLogo}
              alt="deleteicon"
            />
          </Button>
        </Box>
        <Button
          onClick={() => onAddToSelf(item.id, parentId)}
          variant="outline"
          size="sm"
          style={{
            marginLeft: "8px",
            color: "#003D86",
            fontWeight: 700,
            fontSize: "12px",
            textTransform: "capitalize",
            mt: "30px",
            "&:disabled": {
              color: "#003D86",
            },
          }}
          disabled={state?.preview || pathname === "/preview-rule"}
        >
          + Convert to group
        </Button>
      </Box>
    </Box>
  );
};

const CreateRuleCommonPage = ({state}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [flag, setFlag] = useState(false);
  const navigate = useNavigate();
//   const { state } = useLocation();
  const token = getToken();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const [obj, setObj] = useState({ name: "", description: "" });
  const [data, setData] = useState({
    array: [
      {
        id: Date.now(),
        isGroup: false,
        attributeName: null,
        entityName: null,
        attributeId: null,
        type: null,
        valueType: null,
        value: null,
        condition: "OR",
      },
    ],
  });
  const roleOptionData = useSelector(
    (store) => store.CreateRuleReducer.select1
  );
  const roleOption = roleOptionData.map((item) => ({
    id: item?.id,
    name: item?.attributeName,
    attributeValueType: item?.attributeValueType,
    entityName: item?.entityName,
    operator: item?.operator.map((e, index) => {
      return { id: index, name: e };
    }),
  }));
  console.log(data, "data");
  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };
  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };
  // Helper function to handle group promotion
  const handleGroupPromotion = (items) => {
    return items.map((item) => {
      if (item.group) {
        // First, recursively process the groupDetail
        const processedGroupDetail = handleGroupPromotion(item.groupDetail);

        // If there's exactly one child and it's a group
        if (
          processedGroupDetail.length === 1 &&
          processedGroupDetail[0].group
        ) {
          // Promote the child group, keeping parent's ID
          return {
            ...processedGroupDetail[0],
            id: item.id,
          };
        }

        return {
          ...item,
          groupDetail: processedGroupDetail,
        };
      }
      return item;
    });
  };

  const addToMain = () => {
    setData((prev) => ({
      ...prev,
      array: [
        ...prev.array,
        {
          id: Date.now(),
          isGroup: false,
          attributeName: null,
          entityName: null,
          attributeId: null,
          type: null,
          valueType: null,
          value: null,
          condition: "OR",
        },
      ],
    }));
  };

  const addToGroup = (groupId) => {
    setData((prev) => {
      const updateNestedArray = (items) => {
        return items.map((item) => {
          if (item.id === groupId) {
            const lastItem = item.groupDetail[item.groupDetail.length - 1] || {
              name: null,
              condition: "OR",
              group: false,
              groupDetail: [],
              attributeName: null,
              operator: null,
              value: "",
            };

            return {
              ...item,
              groupDetail: [
                ...item.groupDetail,
                {
                  ...lastItem,
                  id: Date.now(),
                  group: false,
                  groupDetail: [],
                  attributeName: null,
                  operator: null,
                  value: "",
                },
              ],
            };
          } else if (item.group) {
            return {
              ...item,
              groupDetail: updateNestedArray(item.groupDetail),
            };
          }
          return item;
        });
      };

      const updatedArray = updateNestedArray(prev.array);
      return {
        ...prev,
        array: handleGroupPromotion(updatedArray),
      };
    });
  };

  const addToSelf = (itemId, parentId) => {
    setData((prev) => {
      const updateNestedArray = (items) => {
        return items.map((item) => {
          if (item.id === itemId) {
            const baseItem = {
              isGroup: false,
              attributeName: item.attributeName,
              entityName: item.entityName,
              attributeId: item.attributeId,
              operator: item.operator,
              type: item.type,
              valueType: item.valueType,
              value: item.value,
              condition: item.condition,
            };

            return {
              ...item,
              group: true,
              groupDetail: [
                { ...baseItem, condition: "OR", id: Date.now() },
                {
                  // ...baseItem,
                  id: Date.now() + 1,
                  isGroup: false,
                  attributeName: null,
                  entityName: null,
                  attributeId: null,
                  type: null,
                  valueType: null,
                  value: null,
                  condition: "OR",
                },
              ],
            };
          } else if (item.group) {
            return {
              ...item,
              groupDetail: updateNestedArray(item.groupDetail),
            };
          }
          return item;
        });
      };

      const updatedArray = updateNestedArray(prev.array);
      return {
        ...prev,
        array: handleGroupPromotion(updatedArray),
      };
    });
  };

  const editItem = (itemId, parentId, key, value) => {
    setData((prev) => {
      const updateNestedArray = (items) => {
        return items.map((item) => {
          if (item.id === itemId) {
            return {
              ...item,
              [key]: value,
            };
          } else if (item.group) {
            return {
              ...item,
              groupDetail: updateNestedArray(item.groupDetail),
            };
          }
          return item;
        });
      };

      return {
        ...prev,
        array: updateNestedArray(prev.array),
      };
    });
  };

  const deleteItem = (itemId, parentId) => {
    setData((prev) => {
      const processItems = (items) => {
        if (!parentId) {
          return items.filter((item) => item.id !== itemId);
        }

        return items.map((item) => {
          if (item.group) {
            if (item.id === parentId) {
              const remainingItems = item.groupDetail.filter(
                (subItem) => subItem.id !== itemId
              );

              if (remainingItems.length === 1) {
                // Return the remaining item, preserving the parent's ID
                return {
                  ...remainingItems[0],
                  id: item.id,
                };
              }

              return {
                ...item,
                groupDetail: remainingItems,
              };
            }

            return {
              ...item,
              groupDetail: processItems(item.groupDetail),
            };
          }

          return item;
        });
      };

      const updatedArray = processItems(prev.array);
      return {
        ...prev,
        array: handleGroupPromotion(updatedArray),
      };
    });
  };
  const handleNameChange = useCallback((e) => {
    setObj((prev) => ({ ...prev, name: e }));
  }, []);

  const handleDescriptionChange = useCallback((e) => {
    setObj((prev) => ({ ...prev, description: e }));
  }, []);

  const handleSave = () => {
    if (!obj.name) {
      toast.error("Rule Name is required");
      return;
    }
    if (!obj.description) {
      toast.error("Rule Description is required");
      return;
    }

    const ruleConfig = convertToRuleConfiguration(data.array);
    if (!ruleConfig) return;
    const postData = {
      name: obj.name,
      description: obj.description,
      type: "EXPRESSION",
      ruleConfiguration: ruleConfig,
    };

    axios
      .post(`${process.env.REACT_APP_API_BASE_URL}/api/create-rule`, postData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        toast.success("Rule created successfully");
        setTimeout(() => {
          if (state?.id === "offer") {
            navigate(-1);
          } else {
            navigate("/view-rules");
          }
        }, 2000);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.message || "Something went wrong");
      });
  };

  const getRuleDetailsById = async (id) => {
    try {
      const response = await axios.get(`${baseUrl}/api/get-rule-by-id/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const { name, description, ruleConfiguration } = response.data;

      // Update the name and description
      setObj({
        name,
        description,
      });

      // Helper function to convert API rule config to component format
      const convertRuleConfigToComponentFormat = (rules) => {
        return rules.map((rule) => {
          if (rule.isGroup) {
            // Handle nested groups
            return {
              id: Date.now(),
              group: true,
              condition: rule.condition?.toUpperCase() || "OR",
              groupDetail: convertRuleConfigToComponentFormat(rule.group),
            };
          } else {
            // Handle individual rules
            const matchingAttribute = roleOption?.find(
              (option) => option.id.toString() === rule.attributeId
            );

            return {
              id: Date.now(),
              isGroup: false,
              attributeName: matchingAttribute
                ? {
                    id: matchingAttribute.id,
                    name: matchingAttribute.name,
                    attributeValueType: matchingAttribute.attributeValueType,
                    entityName: matchingAttribute.entityName,
                    operator: matchingAttribute.operator,
                  }
                : null,
              entityName: rule.entityName,
              attributeId: rule.attributeId,
              operator:
                matchingAttribute?.operator.find(
                  (op) => op.name === rule.type
                ) || null,
              type: rule.type,
              valueType: rule.valueType,
              value: rule.value,
              condition: rule.condition?.toUpperCase() || "OR",
            };
          }
        });
      };

      // Convert and set the data
      setData({
        array: convertToOriginalFormat(ruleConfiguration),
      });

      // toast.success("Rule loaded successfully");
    } catch (error) {
      console.error("Error loading rule:", error);
      toast.error(error?.response?.data?.message || "Unable to load rule");
    }
  };

  useEffect(() => {
    dispatch(getWhereSectionSelectOne("", "", "loyalty"));
  }, [flag]);

  useEffect(() => {
    dispatch({ type: resetCreateRuleData });
    setTimeout(() => {
      if (state?.id && state?.id !== "offer") {
        getRuleDetailsById(state?.id);
      }
      if (!state?.preview) {
        // Clear state after first render
        window.history.replaceState({}, document.title);
      }
    }, 100);
  }, []);

  useEffect(() => {
    if (state?.preview) {
      if (!sessionStorage.getItem("previewRuleVisited")) {
        sessionStorage.setItem("previewRuleVisited", "true");
      } else {
        navigate("/view-rules");
      }
    }

    return () => {
      sessionStorage.removeItem("previewRuleVisited");
    };
  }, [navigate]);

  return (
    <Box
      sx={{
        margin: "20px",
        padding: "20px",
        backgroundColor: "#FFFFFF",
        borderRadius: "8px",
        minHeight: "calc(100vh - 90px)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <CreateRuleAttributeDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        setFlag={setFlag}
      />
      <Box>
        <Box>
          <ToastContainer />
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Button
              sx={{
                minWidth: "0px",
                display: pathname === "/create-rules" ? "none" : "default",
              }}
              // onClick={() =>
              //   navigate("/offers/create-offers/set-qualifying-criteria")
              // }
              onClick={() => navigate(-1)}
            >
              <img
                style={{ height: "16px", width: "16px" }}
                src={arrow}
                alt=""
              />
            </Button>
            <Typography
              sx={{
                fontFamily: "Lato",
                fontWeight: 700,
                fontSize: "16px",
                lineHeight: "20px",
                color: "#0B1B32",
              }}
            >
              Create New Rule
            </Typography>
          </Box>
          {/* rule name and rule description && where start */}
          <Box>
            <Box sx={{ width: "500px" }}>
              <BoldLabel label="Rule Name" isRequired={true}/>
              <MuiInput
                value={obj?.name}
                onChange={(e) => handleNameChange(e)}
                placeholder="50 characters"
                maxLength={50}
                disable={state?.preview || pathname === "/preview-rule"}
              />
            </Box>
            <Box sx={{ width: "500px" }}>
              <BoldLabel label="Rule Description" isRequired={true} />
              <MuiTextarea
                value={obj.description}
                onChange={(e) => handleDescriptionChange(e)}
                placeholder="200 characters"
                maxLength={200}
                isDisable={state?.preview || pathname === "/preview-rule"}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Lato",
                fontWeight: 700,
                fontSize: "16px",
                lineHeight: "20px",
                color: "#0B1B32",
                marginTop: "25px",
                marginBottom: "20px",
                textAlign: "left",
              }}
            >
              Where
            </Typography>

       {  pathname !== "/preview-rule" &&   <Button
              sx={{
                width: "151px",
                height: "28px",
                backgroundColor: "#003D86",
                borderRadius: "6px",
                color: "#FFFFFF",
                "&:hover": {
                  backgroundColor: "#003D86",
                },
                "&:disabled": {
                  backgroundColor: "#003D86",
                  color: "#FFFFFF",
                },
                textTransform: "none",
              }}
              onClick={handleDrawerOpen}
              disabled={state?.preview}
            >
              <FaPlus />
              <Typography
                sx={{
                  fontFamily: "Lato",
                  fontSize: "12px",
                  lineHeight: "14.4px",
                  fontWeight: 500,
                  marginLeft: "6px",
                  textTransform: "capitalize",
                }}
              >
                Add new attribute
              </Typography>
            </Button>}
          </Box>
        </Box>

        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            // border: '1px solid #ccc',
            borderRadius: "8px",
          }}
        >
          {data.array.map((item, index) => (
            <NestedItem
              key={item.id}
              item={item}
              parentId={null}
              parentPath={`${index + 1}`}
              onAddToSelf={addToSelf}
              onAddToGroup={addToGroup}
              onDelete={deleteItem}
              onEdit={editItem}
              depth={0}
              index={index}
              allData={data.array}
              state={state}
              pathname={pathname}
            />
          ))}
        </Box>
        {/* add main item */}

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "14px" }}>
          <Button
            sx={{
              width: "151px",
              height: "28px",
              color: "#003D86",
              fontFamily: "Lato",
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "normal",
              letterSpacing: "0.28px",
              textTransform: "capitalize",
              "&:disabled": {
                color: "#003D86",
              },
            }}
            onClick={addToMain}
            disabled={state?.preview || pathname === "/preview-rule"}
          >
            <FaPlus />
            <Typography
              sx={{
                fontFamily: "Lato",
                fontSize: "12px",
                lineHeight: "14.4px",
                fontWeight: 500,
                marginLeft: "6px",
                textTransform: "capitalize",
              }}
            >
              Create New Criteria
            </Typography>
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          gap: "16px",
          mt: "20px",
        }}
      >
        <Button
          sx={{
            color: "#003D86",
            fontFamily: "Lato",
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: "16.8px",
            backgroundColor: "#E8F0F9",
            width: "136px",
            height: "30px",
            borderRadius: "6px",
            textTransform: "capitalize",
            "&:hover": {
              backgroundColor: "#E8F0F9",
            },
            display: state?.id === "offer" ? "block" : "none",
          }}
          onClick={() => navigate(-1)}
        >
          Cancel
        </Button>
        <Button
          sx={{
            display: pathname === "/preview-rule"? "none" : "block",
            color: "#FFFFFF",
            fontFamily: "Lato",
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: "16.8px",
            backgroundColor: "#003D86",
            width: "136px",
            height: "30px",
            borderRadius: "6px",
            textTransform: "capitalize",
            "&:hover": {
              backgroundColor: "#003D86",
            },
          }}
          onClick={handleSave}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default CreateRuleCommonPage;
